import { useState } from "react";
import { useTestPaymentStore } from "../hooks/use-test-payment-store";
import { TestList } from "./test-list";
import { PaymentSummary } from "./payment-summary";
import { Button } from "@/components/ui/button";
import { HealaLoader } from "@/components/heala-loader";
import { X, Loader2 } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery as useApolloQuery, useLazyQuery, useMutation } from "@apollo/client";
import { getRejectedTestsQuery, prescriptionReferralQuery, getPartnerConfigurationQuery, enrolleeTestOrderMutation, initPaymentMutation, getFeaturedDiagnosticsPartnerQuery } from "@/graphql/queries";
import { TestPaymentData } from "../types";
import { useToast } from "@/hooks/use-toast";
import { HealaLogoWithText } from "@/components/icons/heala-logo-with-text";
import { TestAddressSection } from "./test-address-section";
import { EnrolleeTestOrderInput } from "@/graphql/generated/graphql";

interface TestPaymentPageProps {
  referralId: string;
}

export function TestPaymentPage({ referralId }: TestPaymentPageProps) {
  const {
    paymentData,
    error: storeError,
    testAddress,
    setPaymentData,
    setError,
  } = useTestPaymentStore();

  // State for order processing
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();

  // Mutations for order creation and payment
  const [createTestOrder] = useMutation(enrolleeTestOrderMutation);
  const [initializePayment] = useMutation(initPaymentMutation);

  // Fetch rejected tests data
  const { loading: rejectedTestsLoading, error: queryError } = useApolloQuery(getRejectedTestsQuery, {
    variables: { referralId },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {

      if (!data.getRejectedTests.tests) {
        setError("No test data found");
        return;
      }

      // Step 1: Extract and store access token
      const accessToken = data.getRejectedTests.account?.access_token;
      if (accessToken) {
        sessionStorage.setItem('token', accessToken);
      } else {
        console.warn("No access token found in rejected tests response");
      }

      // Transform the data to match our TestPaymentData structure
      const transformedData: TestPaymentData = {
        id: referralId,
        patientName: "Patient", // This would come from the API in a real implementation
        patientEmail: "patient@example.com", // This would come from the API in a real implementation
        tests: data.getRejectedTests.tests.map(test => ({
          _id: test._id || "",
          testName: test.name || "",
          testPrice: test.price || 0
        })),
      };

      setPaymentData(() => transformedData);


      fetchPrescriptionReferral({
        variables: { id: referralId }
      });

      fetchFeaturedDiagnosticsPartner();
    },
    onError: (error) => {
      console.error("Error fetching rejected tests:", error);
      setError(error.message || "Failed to fetch payment data. Please try again.");
    }
  });

  const [fetchPrescriptionReferral, { data: prescriptionReferralQueryResponse, loading: prescriptionReferralLoading }] = useLazyQuery(prescriptionReferralQuery, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      console.error("Error fetching prescription referral:", error);
    }
  });
  const prescriptionReferralData = prescriptionReferralQueryResponse?.prescriptionReferral;

  const [fetchFeaturedDiagnosticsPartner, { loading: featuredDiagnosticsPartnerLoading, data: featuredDiagnosticsPartnerData }] = useLazyQuery(getFeaturedDiagnosticsPartnerQuery, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.getFeaturedPartner?.providerId) {
        // Execute the next query to get partner configuration
        fetchPartnerConfiguration({
          variables: { partnerId: data.getFeaturedPartner.providerId }
        });
      } else {
        console.error("No featured diagnostics partner found");
        toast({
          title: "Error",
          description: "No featured diagnostics partner found. Please try again later.",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      console.error("Error fetching featured diagnostics partner:", error);
    }
  });

  const [fetchPartnerConfiguration, { loading: partnerConfigurationLoading }] = useLazyQuery(getPartnerConfigurationQuery, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      // Update test prices based on partner markup
      const partnerTestPriceMarkUp = data.getPartnerConfiguration?.testPriceMarkup || null;
      if (partnerTestPriceMarkUp) {
        setPaymentData(prev => {
          if (!prev || !prev.tests) {
            console.error("No payment/test data available to update test prices");
            setError("Failed to compute test price. Please try again.");
            toast({
              title: "Error",
              description: "Failed to compute test price. Please try again.",
              variant: "destructive",
            });
            return prev;
          }

          const updatedTests = prev.tests.map(test => ({
            ...test,
            markedUpTestPrice: (test.testPrice * (partnerTestPriceMarkUp / 100)) + test.testPrice
          }));

          return ({
            ...prev,
            tests: updatedTests
          })
        });
      } else {
        const partnerId = data.getPartnerConfiguration.partner || "Unknown";
        console.error(`Error: No test markup set for partner with id ${partnerId}`);
      }
    },
    onError: (error) => {
      console.error("Error fetching partner configuration:", error);
    }
  });


  // Combine errors from store and query
  const error = storeError || (queryError ? queryError.message : null);
  const isLoading = rejectedTestsLoading ||
    prescriptionReferralLoading ||
    partnerConfigurationLoading ||
    featuredDiagnosticsPartnerLoading;

  const handlePlaceOrder = async () => {
    // Check if we have all required data
    if (!prescriptionReferralData || !paymentData) {
      toast({
        title: "Missing Data",
        description: "Required data is not available. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessingOrder(true);

    try {
      // Step 1: Create test order
      console.log("Creating test order...");

      // Calculate total amount
      const subtotal = paymentData.tests.reduce((sum, test) => sum + ((test.markedUpTestPrice || 0)), 0);
      const totalAmount = subtotal;

      // Check for required fields in prescriptionReferralData and show toast if missing
      const missingFields = [];

      if (!prescriptionReferralData?.patient?.phoneNumber) missingFields.push("patient.phoneNumber");
      if (!prescriptionReferralData?._id) missingFields.push("referralId");
      if (!testAddress?.state) missingFields.push("testAddress.state");
      if (!testAddress?.lga) missingFields.push("testAddress.lga");
      if (!testAddress?.city) missingFields.push("testAddress.city");
      if (!testAddress?.street) missingFields.push("testAddress.street");


      if (missingFields.length > 0) {
        toast({
          title: "Missing Data",
          description: `Required fields/data are missing: ${missingFields.join(", ")}`,
          variant: "destructive",
        });
        setIsProcessingOrder(false);
        return;
      }

      // Prepare test order data
      const testOrderData = {
        testIds: paymentData.tests.map(test => test._id),
        reason: "diagnostic_test",
        sampleCollection: "lab",
        labInfo: {
          name: featuredDiagnosticsPartnerData?.getFeaturedPartner?.name
        },
        referralId: prescriptionReferralData._id,
        userLocation: {
          address: testAddress?.street,
          phoneNumber: prescriptionReferralData?.patient?.phoneNumber || "",
          state: testAddress?.state,
          lga: testAddress?.lga,
          city: testAddress?.city,
          landmark: testAddress?.landmark
        }
      } as EnrolleeTestOrderInput;

      const testOrderResult = await createTestOrder({
        variables: { data: testOrderData }
      });

      if (!testOrderResult.data?.enrolleeTestOrder?.diagnosticTest?._id) {
        throw new Error("Failed to create test order");
      }

      const diagnosticTestId = testOrderResult.data.enrolleeTestOrder.diagnosticTest._id;
      console.log("Test order created successfully:", diagnosticTestId);

      // Step 2: Initialize payment
      console.log("Initializing payment...");

      const paymentInitData = {
        user: prescriptionReferralData?.patient?._id,
        reason: "diagnostic_test",
        amount: totalAmount,
        email: prescriptionReferralData?.patient?.email,
        itemId: diagnosticTestId,
        callback_url: `${window.location.origin}/test-payment/success/${diagnosticTestId}?referralId=${referralId}`,
        saveCard: true
      };

      const paymentResult = await initializePayment({
        // @ts-expect-error string or undefined fields
        variables: { data: paymentInitData }
      });

      if (paymentResult.data?.initPayment?.errors && paymentResult.data.initPayment.errors.length > 0) {
        const errorMessages = paymentResult.data.initPayment.errors.map(err => err.message).join(", ");
        throw new Error(`Payment initialization faileds: ${errorMessages}`);
      }

      if (!paymentResult.data?.initPayment?.paymentInitResponse?.authorization_url) {
        throw new Error("No authorization URL received from payment service");
      }

      // console.log("Payment initialized successfully");

      // Redirect to payment gateway
      window.location.href = paymentResult.data.initPayment.paymentInitResponse.authorization_url;

    } catch (error) {
      console.error("Error processing order:", error);

      setIsProcessingOrder(false);

      toast({
        title: "Order Processing Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    navigate({ to: "/" });
  };


  const subtotal = paymentData?.tests.reduce((sum, test) => sum + (test.markedUpTestPrice || 0), 0);
  const total = (subtotal || 0);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <HealaLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-4">
        <div className="text-red-500 text-xl mb-4">Error: {error}</div>
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-4">
        <div className="text-red-500 text-xl mb-4">Error: No payment data found</div>
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50 p-4">
      {/* Heala Logo */}
      <div className="mb-10 mt-4 lg:mt-6">
        <HealaLogoWithText />
      </div>

      <div className="bg-white rounded-lg shadow-md w-full max-w-2xl relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="p-6">
          <h1 className="text-2xl font-bold mb-6">Checkout</h1>

          <div className="border-t border-gray-200 my-4"></div>

          <TestList tests={paymentData.tests} />

          <div className="border-t border-gray-200 my-4"></div>

          <TestAddressSection />

          <PaymentSummary
            subtotal={subtotal || 0}
            total={total}
          />

          <Button
            className="w-full mt-6 py-6"
            onClick={handlePlaceOrder}
            disabled={isProcessingOrder}
          >
            {isProcessingOrder ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing Order...
              </>
            ) : (
              "Place Order"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

